import type { CityConfig } from "../city-template/types";

export const palmdaleConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Palmdale",
  state: "California",
  stateAbbr: "CA",
  slug: "palmdale",
  route: "/cities/palmdale",
  title: "Emergency Passport Palmdale, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Palmdale, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Antelope Valley and northern Los Angeles County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Palmdale, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Palmdale and Antelope Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Palmdale%20California%20high%20desert%20landscape%20at%20golden%20hour%20with%20Joshua%20trees%20and%20creosote%20bush%20scrubland%2C%20the%20San%20Gabriel%20Mountains%20in%20the%20distance%2C%20wide%20open%20desert%20valley%20with%20dramatic%20sunset%20sky%2C%20suburban%20developments%20along%20the%20Antelope%20Valley%20Freeway%20corridor%2C%20warm%20amber%20and%20orange%20desert%20sunset%20light%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20high%20desert%20environment%20with%20warm%20earth%20tones%20and%20dramatic%20clouds&width=1800&height=700&seq=palmdale-hero-001&orientation=landscape",
  heroImageAlt: "Palmdale California high desert landscape at golden hour with San Gabriel Mountains in the distance",
  geoPosition: "34.5794;-118.1165",
  geoPlacename: "Palmdale, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 70–90 minutes from Palmdale via CA-14 and I-5",
  },
  nearbyCitiesZips: [
    { name: "Palmdale", zips: "93550, 93551, 93552, 93553, 93590, 93591, 93599" },
    { name: "Lancaster", zips: "93534, 93535, 93536, 93539, 93584" },
    { name: "Quartz Hill", zips: "93536" },
    { name: "Rosamond", zips: "93560" },
    { name: "Mojave", zips: "93501" },
    { name: "Acton", zips: "93510" },
    { name: "Littlerock", zips: "93543" },
    { name: "Pearblossom", zips: "93553" },
    { name: "Lake Los Angeles", zips: "93535" },
    { name: "Santa Clarita", zips: "91350, 91351, 91354, 91355" },
    { name: "Tehachapi", zips: "93561" },
    { name: "California City", zips: "93505" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Antelope Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Santa Clarita", route: "/cities/santa-clarita" },
    { name: "Burbank", route: "/cities/burbank" },
    { name: "Glendale", route: "/cities/glendale" },
    { name: "Pasadena", route: "/cities/pasadena" },
    { name: "San Bernardino", route: "/cities/san-bernardino" },
    { name: "Riverside", route: "/cities/riverside" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Palmdale remotely via secure courier. We do not have a walk-in office in Palmdale — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Antelope Valley travelers.",
  fpcOfficeAddress: "200 Spectrum Center Dr, Irvine, CA 92618",
  fpcOfficeDistance: "Serving all of the Antelope Valley and northern Los Angeles County",
  serviceAreaIntro: "From the Palmdale Trade and Commerce Center to the Rancho Vista area, from the Antelope Valley Mall to the aerospace corridor — Fast Passport Center serves the entire Palmdale community. We regularly help travelers from Lancaster, Quartz Hill, Rosamond, and the greater Antelope Valley get their passports fast.",
  nearbyCitiesIntro: "We serve all Palmdale neighborhoods and nearby Antelope Valley communities including Lancaster, Quartz Hill, Rosamond, Littlerock, and Pearblossom. If you are in the Antelope Valley or northern Los Angeles County, we have you covered.",
  howItWorksIntro: "Most Palmdale travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Palmdale Regional Airport and LAX accessible via CA-14 and I-5, Antelope Valley travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Palmdale and Antelope Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Palmdale? Here are the most common ones we hear from Antelope Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Antelope Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert G.",
      location: "Palmdale, CA",
      rating: 5,
      text: "My flight to Dubai was in 2 days and my passport had expired. Fast Passport Center got me a new one in 36 hours. Saved my aerospace industry business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Maria H.",
      location: "Lancaster, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Cancun. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Thomas W.",
      location: "Quartz Hill, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Spain. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Sandra L.",
      location: "Rosamond, CA",
      rating: 5,
      text: "As a frequent international traveler from the Antelope Valley, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "pmd-1",
      name: "Palmdale Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1601 E Palmdale Blvd",
      city: "Palmdale",
      state: "CA",
      zip: "93550",
      phone: "(661) 273-0110",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Palmdale City Hall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "pmd-2",
      name: "Lancaster Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "44701 Date Ave",
      city: "Lancaster",
      state: "CA",
      zip: "93534",
      phone: "(661) 949-4011",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from Lancaster City Park",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "pmd-3",
      name: "Rosamond Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2635 Sierra Hwy",
      city: "Rosamond",
      state: "CA",
      zip: "93560",
      phone: "(661) 256-8111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Rosamond Park",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern Antelope Valley residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "pmd-4",
      name: "Santa Clarita Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "24355 Creekside Rd",
      city: "Santa Clarita",
      state: "CA",
      zip: "91355",
      phone: "(661) 254-0100",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles from Palmdale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Antelope Valley residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "pmd-5",
      name: "Mojave Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "16576 K St",
      city: "Mojave",
      state: "CA",
      zip: "93501",
      phone: "(661) 824-4511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Palmdale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Antelope Valley and Kern County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "pmd-6",
      name: "LA County Clerk — Lancaster",
      type: "County Clerk Passport Acceptance",
      address: "43779 15th St W",
      city: "Lancaster",
      state: "CA",
      zip: "93534",
      phone: "(661) 945-6446",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "2 miles from Palmdale",
      website: "https://www.lavote.net/",
      websiteLabel: "LA County Clerk",
      notes: "County facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Antelope Valley Coverage", agency: "No Antelope Valley expertise", fpc: "Antelope Valley, aerospace industry, and high desert travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Antelope Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Antelope Valley acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Antelope Valley applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Antelope Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Antelope Valley travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Palmdale." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Palmdale address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Palmdale before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Palmdale for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Antelope Valley travelers." },
    { q: "How fast can I renew my passport in Palmdale?", a: "Fast Passport Center offers fast passport renewal in Palmdale in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Palmdale in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Palmdale?", a: "Same-day passport appointments are only available at the Los Angeles Passport Agency (11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024) for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Palmdale residents." },
    { q: "Can I expedite a child passport in Palmdale?", a: "Yes. Fast Passport Center expedites child passports in Palmdale for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Palmdale with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Palmdale?", a: "With Fast Passport Center's emergency courier service, Palmdale travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Los Angeles Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Palmdale?", a: "Popular options include the Palmdale Post Office (93550), Lancaster Post Office (93534), Rosamond Post Office (93560), and the LA County Clerk in Lancaster. All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Palmdale?", a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in Lancaster, Quartz Hill, or Rosamond?", a: "We serve all of the Antelope Valley including Lancaster, Quartz Hill, Rosamond, Littlerock, and Pearblossom. Our overnight shipping labels work from any UPS location, so your location within the Antelope Valley doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Palmdale?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Palmdale?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Palmdale and Lancaster offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Palmdale?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Palmdale?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Antelope Valley families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Palmdale", address: "39344 10th St W, Palmdale, CA 93551", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Lancaster", address: "44801 10th St W, Lancaster, CA 93534", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Palmdale", address: "39838 10th St W, Palmdale, CA 93551", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Quartz Hill", address: "42104 50th St W, Quartz Hill, CA 93536", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Palmdale", address: "37140 47th St E, Palmdale, CA 93552", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Rosamond", address: "2665 Sierra Hwy, Rosamond, CA 93560", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Lancaster", address: "43730 20th St W, Lancaster, CA 93534", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Lancaster", address: "1141 W Ave L, Lancaster, CA 93534", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Palmdale",
      address: "1601 E Palmdale Blvd, Palmdale, CA 93550",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Lancaster",
      address: "43779 15th St W, Lancaster, CA 93534",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Santa Clarita",
      address: "20725 Soledad Canyon Rd, Santa Clarita, CA 91351",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Bakersfield",
      address: "3120 F St, Bakersfield, CA 93301",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Kern County residents near the Antelope Valley.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PMD",
      name: "Palmdale Regional Airport",
      distance: "6 miles from downtown Palmdale",
      address: "41000 20th St E, Palmdale, CA 93550",
      note: "Local airport with limited commercial service. Serves Antelope Valley travelers.",
      flightUrl: "https://www.cityofpalmdale.org/",
      flightLabel: "Palmdale Airport",
      featured: true,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "45 miles from Palmdale",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Domestic flights and some international connections. Accessible via CA-14 and I-5.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "65 miles from Palmdale",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via CA-14 and I-405.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [{ icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from California CDPH Vital Records for Los Angeles County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the LA County Registrar-Recorder.", url: "https://www.lavote.gov/records/" }, { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through LA County Superior Court.", url: "https://www.lacourt.org/" }],
};